import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_THEME, getTheme, themes } from '../config/themes';

// The default value lets components render outside a provider (e.g. in isolated tests).
const ThemeContext = createContext({
    currentBrand: DEFAULT_THEME,
    theme: themes[DEFAULT_THEME],
    bodyClass: '',
    pathname: null,
    setBodyClass: () => {},
});

/** Theme key for a URL: `/woolies` → 'woolies', anything that isn't a theme → 'default'. */
// eslint-disable-next-line react-refresh/only-export-components
export const brandFromPath = (pathname = '/') => {
    const key = pathname.split('/')[1];
    return themes[key] ? key : DEFAULT_THEME;
};

/** The palette as a `:root { --var: value }` rule, so it's part of the prerendered HTML. */
// eslint-disable-next-line react-refresh/only-export-components
export const themeCss = (brand) => {
    const declarations = Object.entries(getTheme(brand).colors)
        .map(([key, value]) => `--${key}:${value};`)
        .join('');
    return `:root{${declarations}}`;
};

/**
 * Owns global page styling, rendered declaratively (and therefore prerendered):
 * - the brand palette, derived from the URL (see ThemeStyle)
 * - the body class and body[data-brand] (see ThemedBody)
 *
 * The body class is the current route's `handle.bodyClass` (e.g. 'site-info'), unless the
 * page declares its own with useBodyClass (App's 'day-for-it' / 'pub-time'). A declaration
 * only applies on the pathname it was made for, so navigating away never leaves it stale.
 *
 * Rendered by the root Layout around <html>, which passes the router state in as props.
 */
export function ThemeProvider({ pathname = '/', routeBodyClass = '', children }) {
    const [declared, setDeclared] = useState(null); // { pathname, className }

    const setBodyClass = useCallback((className, forPathname) => {
        setDeclared({ pathname: forPathname, className });
    }, []);

    const currentBrand = brandFromPath(pathname);
    const bodyClass = declared?.pathname === pathname ? declared.className : routeBodyClass;

    const value = useMemo(() => ({
        currentBrand,
        theme: getTheme(currentBrand),
        bodyClass,
        pathname,
        setBodyClass,
    }), [currentBrand, bodyClass, pathname, setBodyClass]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);

/**
 * Declare the body class for the current page. Pass '' for no class.
 * `undefined` means "this component doesn't own the body class" and is ignored.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBodyClass = (className) => {
    const { pathname, setBodyClass } = useTheme();

    useEffect(() => {
        if (className === undefined) return;
        setBodyClass(className, pathname);
    }, [className, pathname, setBodyClass]);
};

/** Renders the current brand's palette as CSS custom properties. Place in <head>. */
export function ThemeStyle() {
    const { currentBrand } = useTheme();
    return <style id="theme-palette">{themeCss(currentBrand)}</style>;
}

/** The document <body>, carrying the current body class and brand. */
export function ThemedBody({ children }) {
    const { bodyClass, currentBrand } = useTheme();
    return (
        <body className={bodyClass || undefined} data-brand={currentBrand}>
            {children}
        </body>
    );
}
